import { css, cx } from "@emotion/css"
import { useEffect, useState } from "react"

import useActive from "../../hooks/useActive"
import useHover from "../../hooks/useHover"

const useLongPress = (isActive: boolean, callback?: () => void) => {
  const [isLongPressed, setIsLongPressed] = useState(false)

  useEffect(() => {
    if (!callback) return

    if (isActive) {
      callback()
      const timerId = window.setTimeout(() => setIsLongPressed(true), 200)
      return () => window.clearTimeout(timerId)
    }
    setIsLongPressed(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  useEffect(() => {
    if (!callback) return

    if (isLongPressed) {
      const timerId = window.setInterval(callback, 50)
      return () => window.clearInterval(timerId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLongPressed])
}

type Props = {
  hoverClassName?: string
  activeClassName?: string
  render?: (state: { isHovered: boolean; isActive: boolean }) => JSX.Element
  onLongPress?: () => void
} & JSX.IntrinsicElements["button"]

const Button = ({
  type = "button",
  className,
  hoverClassName,
  activeClassName,
  render,
  children,
  onLongPress,
  ...props
}: Props) => {
  const { isHovered, hoverProps } = useHover()
  const { isActive, activeProps } = useActive()

  useLongPress(isActive, onLongPress)

  return (
    <button
      type={type}
      {...props}
      {...hoverProps}
      {...activeProps}
      className={cx(style, className, isHovered && hoverClassName, isActive && activeClassName)}
    >
      {render ? render({ isHovered, isActive }) : children}
    </button>
  )
}

export default Button

const style = css`
  font-family: inherit;
`
